import classNames from "classnames/bind";
import { clamp, get } from "es-toolkit/compat";
import React, { useCallback, useEffect, useRef, useState } from "react";
import FormInputWrapper from "../Form/FormInputs/FormInputWrapper";
import { Menu } from "../Menu";
import FilterSwitcher from "./FilterSwitcher";
import styles from "./FilterSwitcherWithMenuEdit.module.scss";
import {
  ControlledFilterSwitcherWithMenuEditProps,
  FilterSwitcherWithMenuEditProps,
} from "./types";

const cx = classNames.bind(styles);

/**
 * A filter component presented as a context switcher, with editing capability through a menu
 */
const FilterSwitcherWithMenuEdit = ({
  options = [],
  labelKey,
  valueKey,
  onEdit,
  onChange,
  ...restProps
}: FilterSwitcherWithMenuEditProps) => {
  const { value } = restProps;
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const getLabel = (option: Record<string, any> | string) => {
    if (typeof option === "string") {
      return option;
    } else {
      return get(option, labelKey!);
    }
  };

  const getValue = (option: Record<string, any> | string) => {
    if (typeof option === "string") {
      return option;
    } else {
      return get(option, valueKey!);
    }
  };

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        !containerRef ||
        containerRef?.current?.contains?.(event.target as HTMLDivElement)
      ) {
        return;
      }

      setIsMenuOpen(false);
    },
    [containerRef]
  );

  const handleKeyDownInMenu = (evt: React.KeyboardEvent) => {
    const { code, target, shiftKey } = evt;
    const optionElements = (
      target as HTMLElement
    ).parentNode?.querySelectorAll<HTMLLIElement>(
      "[data-filter-switcher-menu-item]"
    );

    if (!optionElements) {
      return;
    }

    const currentOptionIndex = (target as HTMLElement).getAttribute(
      "data-filter-switcher-menu-item"
    )
      ? Array.from(optionElements?.values() ?? []).indexOf(
          target as HTMLLIElement
        )
      : -1;

    switch (code) {
      case "Escape":
        setIsMenuOpen(false);
        break;
      // prevent Tab from doing default browser behavior and trap navigation
      case "Tab":
        evt.preventDefault();
        optionElements
          .item(
            clamp(
              currentOptionIndex + (shiftKey ? -1 : 1),
              0,
              optionElements.length - 1
            )
          )
          ?.focus();
        break;
      case "ArrowUp":
        optionElements
          .item(
            currentOptionIndex > 0
              ? currentOptionIndex - 1
              : optionElements.length - 1
          )
          ?.focus();
        break;
      case "ArrowDown":
        optionElements
          .item(
            currentOptionIndex < optionElements.length - 1
              ? currentOptionIndex + 1
              : 0
          )
          ?.focus();
        break;
      case "Enter":
        optionElements.item(currentOptionIndex).click();
        break;
    }
  };

  const handleEditClick = () => {
    onEdit?.(value ?? "");
    setIsMenuOpen((prevValue) => !prevValue);
  };

  const handleMenuOptionClick = (value: string) => {
    onChange?.(value);
    setIsMenuOpen(false);
    containerRef.current?.focus();
  };

  // close the menu when a click is registered outside the menu target
  useEffect(() => {
    document.addEventListener("click", handleClickOutside);

    return () => document.removeEventListener("click", handleClickOutside);
  }, [handleClickOutside]);

  return (
    <Menu
      isFluid={true}
      isOpen={isMenuOpen}
      onClose={() => setIsMenuOpen(false)}
      target={containerRef.current ?? undefined}
      content={
        <ol
          className={cx("menuOptions")}
          role="listbox"
          tabIndex={0}
          onKeyDown={handleKeyDownInMenu}
        >
          {options.length && labelKey && valueKey ? (
            options.map((opt) => (
              <li
                key={getValue(opt)}
                onClick={() => handleMenuOptionClick(getValue(opt))}
                className={cx("menuOption")}
                tabIndex={0}
                role="option"
                data-filter-switcher-menu-item
              >
                {getLabel(opt)}
              </li>
            ))
          ) : (
            <li className={cx("menuOption", "empty")}>No options available</li>
          )}
        </ol>
      }
    >
      {/* IMPROVE: there's a design issue with the way the Menu component works that prevents us from attaching a ref to the first child */}
      <div className={cx("menuTargetWrapper")}>
        <div ref={containerRef}>
          <FilterSwitcher
            {...restProps}
            onEdit={handleEditClick}
            isEditable={Boolean(options.length)}
          />
        </div>
      </div>
    </Menu>
  );
};

const ControlledFilterSwitcherWithMenuEdit = (
  props: ControlledFilterSwitcherWithMenuEditProps
) => (
  <FormInputWrapper
    {...props}
    isLabelShown={false}
    as={FilterSwitcherWithMenuEdit}
  />
);

export default FilterSwitcherWithMenuEdit;
export { ControlledFilterSwitcherWithMenuEdit };
